import type { OnDestroy } from '@angular/core';
import { inject, Injectable, signal } from '@angular/core';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { WINDOW } from '@app/core/window.token';
import { ApplePayAddressService } from './apple-pay-address.service';
import { ApplePayEmailValidationService } from './apple-pay-email-validation.service';
import { ApplePayTelemetryService } from './apple-pay-telemetry.service';
import type { Client } from 'braintree-web';
import braintree from 'braintree-web';
import type { ApplePay, ApplePayPayload, ApplePayPaymentRequest } from 'braintree-web/apple-pay';
import type { BraintreeError } from 'braintree-web/core';
import type { IShoppingCartStore } from '@app/interface/cart/shopping-cart-store.interface';
import type { RouterService } from '@app/interface/service/router-service.interface';
import type { IJobStore } from '@app/interface/job/job-store.interface';
import type { PaymentService } from '@app/interface/payment/payment-service.interface';
import type { BlockService } from '@app/interface/service/block-service.interface';
import type { AnalyticsService } from '@app/interface/service/analytics-service.interface';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import { EMOJI_REGEX } from '@app/utils/sanitize';

@Injectable()
export class ApplePayCheckoutService implements OnDestroy {
  private readonly shoppingCartStore: IShoppingCartStore = inject(angularjs.ShoppingCartStore);
  private readonly routerService: RouterService = inject(angularjs.RouterService);
  private readonly paymentService: PaymentService = inject(angularjs.PaymentService);
  private readonly jobStore: IJobStore = inject(angularjs.JobStore);
  private readonly blockUiService: BlockService = inject(angularjs.BlockService);
  private readonly userProfileStore: any = inject(angularjs.UserProfileStore);
  private readonly addressService: ApplePayAddressService = inject(ApplePayAddressService);
  private readonly emailValidationService: ApplePayEmailValidationService = inject(ApplePayEmailValidationService);
  private readonly telemetryService: ApplePayTelemetryService = inject(ApplePayTelemetryService);
  private readonly rootScope: ng.IRootScopeService = inject(angularjs.$rootScope);
  private readonly window: Window = inject(WINDOW);
  private readonly analyticsService: AnalyticsService = inject(angularjs.AnalyticsService);
  private readonly contentStore: IContentStore = inject(angularjs.ContentStore);

  private clientInstance: Client | null = null;
  private stopTaxWatch: (() => void) | null = null;
  /**
   * True while the post-authorization reprice+tax pass (`repriceAndRecalculateTax`) is
   * in flight, so the tax `$watch` below can skip its own redundant recompute.
   */
  private paymentInProgress = false;

  /**
   * True while an express cart-edit tax recompute is in flight. The button consumes
   * this to stay un-tappable until the recompute settles, so the sheet can never be
   * opened with a stale total after an edit.
   */
  readonly isRecomputingTax = signal<boolean>(false);
  private taxRecomputeInFlight = 0;

  get isSupported(): boolean {
    return (
      this.window.paymentDisplayApplePayFlag === 'Y' &&
      !!this.window.ApplePaySession &&
      this.window.ApplePaySession.supportsVersion(3) &&
      this.window.ApplePaySession.canMakePayments()
    );
  }

  initialize(express = false): Promise<void> {
    if (!this.isSupported) {
      return Promise.reject('not-supported');
    }
    if (this.window.braintreeAllPaymentsFailFlag) {
      const paymentError = 'All Payments loading failed';
      this.telemetryService.reportBraintreeInitFailure(paymentError);
      return Promise.reject('braintree-disabled');
    }

    return this.paymentService
      .getPaymentInstancePromise()
      .then((clientInstance: Client) => {
        this.clientInstance = clientInstance;
        // Express carts (shopping-cart page) are editable, so keep the pre-calc
        // fresh as the cart changes. The payment-info page is static and skips this.
        // eslint-disable-next-line promise/always-return
        if (express) {
          this.registerTaxWatch();
        }
        // Fold tax into the total before the sheet can be opened (billing-only carts).
        // Fire-and-forget via recomputeTaxTracked so isRecomputingTax gates the button
        // (same signal as watch-triggered recomputes) rather than blocking initialize().
        // Non-express (payment-info) reprices first so the total is always fresh on load;
        // express skips the reprice because the cart page's own edit flow already reprices.
        this.recomputeTaxTracked(!express);
      })
      .catch((err?: BraintreeError) => {
        const paymentError = err?.message ?? 'All Payments loading failed';
        this.telemetryService.reportBraintreeInitFailure(paymentError);
        throw new Error('braintree-init-failed');
      });
  }

  ngOnDestroy(): void {
    this.stopTaxWatch?.();
    this.stopTaxWatch = null;
  }

  /**
   * Computes tax for tax-eligible billing-only carts so the Apple Pay sheet is built
   * tax-inclusive. Runs on page load (and on cart edits for express) — never in the
   * click gesture — so `session.begin()` stays synchronous and Apple Pay is not rejected.
   *
   * Display-only and best-effort: the unchanged post-authorization
   * `repriceCart().then(calculateTax)` remains the source of truth for the charge and
   * for the server-side "Cart Value Tampered With" check, so a failure here never
   * under-collects — the sheet simply shows the pre-tax total in that rare case.
   *
   * Shipping-required carts are excluded on reprice=false(express init): they correct tax in-sheet via
   * `onshippingcontactselected` using the address the customer picks.
   *
   * Pass reprice=true (non-express init) to unconditionally run repriceCart then calculateTax,
   * regardless of hasShippingAddress or taxable-item state. The non-express flow pre-inserts the
   * shipping/billing address we already have on file straight into the payment request (see
   * `createSession`), rather than letting the customer pick it in-sheet, so Apple Pay never fires
   * `onshippingcontactselected` here — that's the only place tax would otherwise be corrected for a
   * shipping-required cart. This is our one chance to get tax right before the sheet opens.
   */
  private precalculateTax(reprice = false): Promise<void> {
    if (this.shoppingCartStore.hasShippingAddress() && !reprice) {
      return Promise.resolve();
    }
    if (!reprice && this.shoppingCartStore.buildSubjectListForTaxSubmission().length === 0) {
      return Promise.resolve();
    }
    if (reprice || this.shoppingCartStore.getRepriceVersion() === 0) {
      return this.shoppingCartStore
        .repriceCart()
        .then(() => this.shoppingCartStore.calculateTax())
        .catch((err: any) => {
          this.telemetryService.reportRepriceTaxError(err);
        });
    }
    return this.shoppingCartStore.calculateTax().catch((err: any) => {
      this.telemetryService.reportRepriceTaxError(err);
    });
  }

  /**
   * Re-runs the pre-calc whenever a reprice completes (i.e. after a cart edit reprices).
   * Watching the reprice-completion counter — rather than the pre-tax total — guarantees
   * shippingAddressRequired and prices have already settled when the recompute reads them,
   * so a cart that reprices into shipping-required is correctly skipped. It also avoids a
   * feedback loop, since calculateTax goes through adjustTax (not adjustPricing) and never
   * bumps the counter.
   *
   * Skips the recompute while `paymentInProgress` is true: the post-authorization reprice
   * in `repriceAndRecalculateTax` also bumps this same counter, and without the guard the
   * watch would fire a second, racing `calculateTax()` call against the payment flow's own.
   */
  private registerTaxWatch(): void {
    this.stopTaxWatch = this.rootScope.$watch(
      () => this.shoppingCartStore.getRepriceVersion(),
      (newVersion: number, oldVersion: number) => {
        if (newVersion !== oldVersion && !this.paymentInProgress) {
          this.recomputeTaxTracked();
        }
      }
    );
  }

  /**
   * Fires a pre-calc while flagging the button as not-ready via isRecomputingTax.
   * Used on init and on watch-triggered recomputes (express cart edits). An integer
   * counter keeps the button blocked until every overlapping recompute settles.
   * Pass reprice=true (non-express init) to run repriceCart before calculateTax.
   */
  private recomputeTaxTracked(reprice = false): void {
    this.taxRecomputeInFlight++;
    this.syncRecomputeSignal();
    void this.precalculateTax(reprice).finally(() => {
      this.taxRecomputeInFlight--;
      this.syncRecomputeSignal();
    });
  }

  /**
   * Mirrors the in-flight counter into the signal on a fresh microtask so the write is
   * never nested inside a change-detection pass (avoids NG0600). Reads the counter at
   * execution time so the signal always reflects the current state.
   */
  private syncRecomputeSignal(): void {
    queueMicrotask(() => this.isRecomputingTax.set(this.taxRecomputeInFlight > 0));
  }

  startSession(express: boolean): void {
    this.telemetryService.reportApplePayStart();

    const shippingAddressRequired = this.shoppingCartStore.hasShippingAddress();

    braintree.applePay.create(
      { client: this.clientInstance! },
      (applePayErr?: BraintreeError, applePayInstance?: ApplePay) => {
        if (applePayErr || !applePayInstance || this.window.braintreeApplePayFailFlag) {
          const paymentError: string = applePayErr?.message ?? 'applepay loading failed';
          this.telemetryService.reportApplePayInitFailure(paymentError);
          return;
        }

        const session = this.createSession(applePayInstance, shippingAddressRequired, express);
        this.attachHandlers(session, applePayInstance, shippingAddressRequired);
        session.begin();
      }
    );
  }

  private createSession(
    applePayInstance: ApplePay,
    shippingAddressRequired: boolean,
    express: boolean
  ): ApplePaySession {
    const paymentRequest: ApplePayPaymentRequest = applePayInstance.createPaymentRequest({
      total: {
        label: 'MyLifetouch',
        amount: this.shoppingCartStore.getTotal().toFixed(2)
      },
      currencyCode: this.jobStore.getCurrency()
    });

    paymentRequest.requiredShippingContactFields = ['name', 'email', 'phone'];

    if (shippingAddressRequired) {
      paymentRequest.requiredShippingContactFields.push('postalAddress');
    } else {
      paymentRequest.requiredBillingContactFields = ['postalAddress'];
    }

    if (!express) {
      if (this.shoppingCartStore.getBillingAddress()?.postalCode) {
        paymentRequest.billingContact = this.addressService.toApplePay(this.shoppingCartStore.getBillingAddress());
      }

      if (this.shoppingCartStore.getShippingAddress()?.postalCode) {
        paymentRequest.shippingContact = this.addressService.toApplePay(this.shoppingCartStore.getShippingAddress());
      }
    }
    // @ts-ignore - Braintree and Apple Pay SDK have conflicting ApplePayPaymentRequest types
    return new this.window.ApplePaySession(3, paymentRequest);
  }

  private attachHandlers(session: ApplePaySession, applePayInstance: ApplePay, shippingAddressRequired: boolean): void {
    session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
      this.handlePaymentAuthorized(event, session, applePayInstance, shippingAddressRequired);
    };

    session.onvalidatemerchant = (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
      applePayInstance.performValidation(
        { validationURL: event.validationURL, displayName: 'MyLifetouch' },
        (err?: BraintreeError, merchantSession?: unknown) => {
          if (err) {
            this.telemetryService.reportBraintreeFailedToValidateMerchant(err);
            return;
          }
          session.completeMerchantValidation(merchantSession);
        }
      );
    };

    if (shippingAddressRequired) {
      session.onshippingcontactselected = (event: ApplePayJS.ApplePayShippingContactSelectedEvent) => {
        this.handleShippingContactSelected(event, session);
      };
    }

    session.oncancel = () => {
      this.telemetryService.reportApplePayCancel();
      this.shoppingCartStore.resetApplePay();
    };
  }

  private handlePaymentAuthorized(
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
    session: ApplePaySession,
    applePayInstance: ApplePay,
    shippingAddressRequired: boolean
  ): void {
    applePayInstance.tokenize(
      { token: event.payment.token },
      (tokenizeErr?: BraintreeError, payload?: ApplePayPayload) => {
        if (tokenizeErr) {
          this.telemetryService.reportFailedToTokenize(tokenizeErr);
          session.completePayment(this.window.ApplePaySession.STATUS_FAILURE);
          return;
        }

        let paymentAddress = shippingAddressRequired ? event.payment.shippingContact : event.payment.billingContact;

        if (!shippingAddressRequired && event.payment.shippingContact) {
          paymentAddress = {
            ...paymentAddress,
            emailAddress: event.payment.shippingContact.emailAddress,
            phoneNumber: event.payment.shippingContact.phoneNumber,
            givenName: event.payment.shippingContact.givenName ?? paymentAddress?.givenName,
            familyName: event.payment.shippingContact.familyName ?? paymentAddress?.familyName
          };
        }

        if (this.hasEmojiInName(paymentAddress)) {
          this.showInvalidNameError(session);
          return;
        }

        void this.emailValidationService.isValid(paymentAddress?.emailAddress).then((isValid) => {
          if (!isValid) {
            this.showInvalidEmailError(session);
            return;
          }

          void this.handleCheckout(paymentAddress, payload, shippingAddressRequired, session);
        });
      }
    );
  }

  /**
   * Reprices then recomputes tax as the post-authorization source-of-truth pass (see
   * `precalculateTax` doc comment). Sets `paymentInProgress` for the duration so the
   * express cart's tax `$watch` (registered in `registerTaxWatch`) skips its own
   * redundant recompute when this reprice bumps `getRepriceVersion()` — otherwise the
   * watch would fire a second, racing `calculateTax()` call against this one.
   */
  private repriceAndRecalculateTax(): Promise<void> {
    this.paymentInProgress = true;
    return this.shoppingCartStore
      .repriceCart()
      .then(() => {
        const taxItems = this.shoppingCartStore.buildSubjectListForTaxSubmission();
        return taxItems.length > 0 ? this.shoppingCartStore.calculateTax() : null;
      })
      .finally(() => {
        this.paymentInProgress = false;
      });
  }

  /**
   * Apple Pay can reject completion when Safari has already closed the sheet. Completion must
   * never prevent the checkout outcome from being recorded or routed to the customer.
   */
  private completePaymentSafely(
    session: ApplePaySession,
    result: Parameters<ApplePaySession['completePayment']>[0]
  ): void {
    try {
      session.completePayment(result);
    } catch (err: unknown) {
      this.telemetryService.reportPaymentCompletionError(err);
    }
  }

  private showInvalidEmailError(session: ApplePaySession): void {
    this.telemetryService.reportInvalidEmail();
    session.completePayment({
      status: this.window.ApplePaySession.STATUS_FAILURE,
      errors: [
        new this.window.ApplePayError('shippingContactInvalid', 'emailAddress', 'Please enter a valid email address')
      ]
    });
  }

  private hasEmojiInName(contact: ApplePayJS.ApplePayPaymentContact | undefined): boolean {
    return [contact?.givenName, contact?.familyName].some((name) => {
      EMOJI_REGEX.lastIndex = 0;
      return Boolean(name && EMOJI_REGEX.test(name));
    });
  }

  private showInvalidNameError(session: ApplePaySession): void {
    session.completePayment({
      status: this.window.ApplePaySession.STATUS_FAILURE,
      errors: [
        new this.window.ApplePayError(
          'shippingContactInvalid',
          'name',
          this.contentStore.translate('MOBILE_TRANS_BILLING_INFO.APPLE_PAY_INVALID_NAME')
        )
      ]
    });
  }

  private async validateShippingAddress(
    paymentAddress: ApplePayJS.ApplePayPaymentContact | undefined,
    shippingAddressRequired: boolean
  ) {
    if (shippingAddressRequired) {
      return this.addressService.validateAddress(paymentAddress, shippingAddressRequired);
    }
    return { valid: true, errors: [] };
  }

  /**
   * On a ship-to-school (shipping-address-not-required) express cart, the payment address
   * comes solely from Apple Pay's billingContact (see `handlePaymentAuthorized`) and is never
   * run through `addressService.validateAddress` (that only validates the shipping-required path). Some
   * regions/devices can hand back a billingContact with no postalCode despite
   * `requiredBillingContactFields` asking for one, which would otherwise submit an order with
   * an empty billing address. Bail out to billing-info (mirroring ReviewOrderCtrl's
   * `!billingAddress` guard, which this flow never reaches) rather than let that through.
   */
  private async handleCheckout(
    paymentAddress: ApplePayJS.ApplePayPaymentContact | undefined,
    payload: ApplePayPayload | undefined,
    shippingAddressRequired: boolean,
    session: ApplePaySession
  ): Promise<void> {
    if (!shippingAddressRequired && !this.addressService.hasRequiredAddressFields(paymentAddress)) {
      this.telemetryService.reportMissingBillingAddress();
      session.completePayment(this.window.ApplePaySession.STATUS_FAILURE);
      this.routerService.goToBillingInfoWithMessage('MISSING_INVALID_INFORMATION');
      return;
    }

    try {
      const { valid, errors } = await this.validateShippingAddress(paymentAddress, shippingAddressRequired);
      if (!valid) {
        session.completePayment({ status: this.window.ApplePaySession.STATUS_FAILURE, errors });
        return;
      }

      this.shoppingCartStore.setApplePay(payload, this.addressService.fromApplePay(paymentAddress));
      await this.repriceAndRecalculateTax();
      this.setGuestProfileFromContact(paymentAddress);
      await this.submitAndRoute(session);
    } catch (err: unknown) {
      this.telemetryService.reportRepriceTaxError(err);
      session.completePayment(this.window.ApplePaySession.STATUS_FAILURE);
    }
  }

  private handleShippingContactSelected(
    event: ApplePayJS.ApplePayShippingContactSelectedEvent,
    session: ApplePaySession
  ): void {
    this.shoppingCartStore.setApplePayAddress(this.addressService.fromApplePay(event.shippingContact));

    this.shoppingCartStore
      .repriceCart()
      .then(() => {
        const taxItems = this.shoppingCartStore.buildSubjectListForTaxSubmission();
        return taxItems.length > 0 ? this.shoppingCartStore.calculateTax() : null;
      })
      .then(() => {
        session.completeShippingContactSelection({
          newTotal: {
            label: 'MyLifetouch',
            amount: this.shoppingCartStore.getTotal().toFixed(2)
          },
          newLineItems: [],
          newShippingMethods: []
        });
      })
      .catch((err: any) => {
        this.telemetryService.reportShippingRepricingError(err);
        session.completeShippingContactSelection({
          errors: [
            new this.window.ApplePayError(
              'shippingContactInvalid',
              'postalAddress',
              'Unable to calculate shipping for this address'
            )
          ],
          newTotal: {
            label: 'MyLifetouch',
            amount: this.shoppingCartStore.getTotal().toFixed(2)
          },
          newLineItems: [],
          newShippingMethods: []
        });
      });
  }

  private submitAndRoute(session: ApplePaySession): Promise<any> {
    this.blockUiService.blockUI();
    return this.shoppingCartStore
      .submitPayment()
      .then((response: any) => {
        if (response?.data?.success) {
          this.analyticsService.recordOrder(response?.data);
          this.analyticsService.setOrderedSubjects(this.shoppingCartStore.getSubjects());
          this.completePaymentSafely(session, this.window.ApplePaySession.STATUS_SUCCESS);
          this.routerService.goToOrderReceipt();
        } else {
          this.completePaymentSafely(session, this.window.ApplePaySession.STATUS_FAILURE);
          const err = response?.data?.message;
          this.telemetryService.reportOrderSubmissionFailure(err);
          this.routerService.goToPaymentInfoWithMessage('PAYMENTHANDLER_SYSTEM_ERROR');
        }
        return;
      })
      .catch((err: any) => {
        this.completePaymentSafely(session, this.window.ApplePaySession.STATUS_FAILURE);
        this.telemetryService.reportOrderSubmissionError(err);
        this.routerService.goToPaymentInfoWithMessage('PAYMENTHANDLER_SYSTEM_ERROR');
      })
      .finally(() => {
        this.blockUiService.unblockUI();
        // Braintree callbacks run outside the AngularJS lifecycle, so flush the
        // $location change before the stale payment page can start another session.
        this.rootScope.$digest();
      });
  }

  private setGuestProfileFromContact(contact: ApplePayJS.ApplePayPaymentContact | undefined): void {
    if (!contact || this.userProfileStore.isLoggedIn()) {
      return;
    }
    const profile = this.userProfileStore.userProfile;
    profile.email = contact.emailAddress;
    profile.firstName = contact.givenName;
    profile.lastName = contact.familyName;
    profile.isGuestProfile = true;
  }
}
